/**
 * shared.ts — Tipos e helpers do domínio de Despesas.
 *
 * Despesas têm duas origens:
 *  - 'campo'      : lançadas pelo instalador no formulário público (sem login).
 *  - 'financeiro' : lançadas internamente pelo financeiro (tela logada).
 */
import { formatarDinheiro } from '@/lib/numero';

export type OrigemDespesa = 'campo' | 'financeiro';

/**
 * Contexto de categoria — de que lista de gastos a pessoa precisa.
 *
 * `todos` é o coringa (combustível, pedágio, alimentação: serve a qualquer um
 * que gaste). `campo` é quem instala; `socios` é quem viaja a trabalho. Já se
 * sabe que virão outros — vendedor lança despesa e faz viagem de curso —, e é
 * por isso que o coringa se chama `todos` e não "ambas".
 */
export type ContextoCategoria = 'todos' | 'campo' | 'socios';

export interface Categoria {
  id: number;
  nome: string;
  codigo?: string | null;
  contexto?: ContextoCategoria | null;
  /** "Quando usar esta e não outra" — vira o title do item na tela. */
  criterio?: string | null;
}

/**
 * Espelha `contexto_do_perfil` do backend (servico_despesas.py), que é a
 * autoridade. Aqui serve só para a TELA decidir o que destacar — o instalador
 * lança quase sempre por uma OS, quem viaja a trabalho quase nunca.
 */
export const contextoDoPerfil = (perfil?: string | null): ContextoCategoria =>
  perfil === 'instalador' ? 'campo' : 'socios';

export interface InstaladorOpcao {
  id: number;
  nome: string;
}

export interface ViagemOpcao {
  id: number;
  label: string;
  loja_id?: number | null; // loja dona da viagem (a despesa herda)
}

export interface OSOpcao {
  id: number;
  codigo: string;
  cliente_nome?: string | null;
  cidade?: string | null;
}

export interface Loja {
  id: number;
  /** "CasaA Búzios" — só onde precisa ser formal (PDF, documento). */
  nome: string;
  /** "BZ" — badge, coluna estreita. */
  codigo?: string | null;
  /** "Búzios" — é este que as telas mostram (migration 051). */
  nome_curto?: string | null;
}

/** Opções do formulário público (GET /public/despesas/opcoes). */
export interface OpcoesPublico {
  categorias: Categoria[];
  instaladores: InstaladorOpcao[];
  viagens: ViagemOpcao[];
  lojas: Loja[];
}

/** Resposta do upload de comprovante (público e interno). */
export interface UploadComprovante {
  arquivo: string;
  url: string;
}

/** Corpo de criação de despesa (público e interno). */
export interface DespesaIn {
  data: string; // 'YYYY-MM-DD'
  valor: number;
  /** Linha curta do que foi ("Pedágio - Rio") — o mesmo campo do Tiny. */
  historico?: string | null;
  descricao?: string | null;
  categoria_id?: number | null;
  loja_id?: number | null;
  viagem_id?: number | null;
  os_id?: number | null;
  usuario_id?: number | null;
  lancado_por?: string | null;
  forma_pgto?: string | null; // código da forma (tbl_app_despesa_formas_pgto)
  loja_cartao_id?: number | null; // cartão de qual loja (só p/ 'Cartão da loja')
  comprovante?: string | null; // string do upload (campo "arquivo")
  lat?: number | null;
  lng?: number | null;
}

/** Despesa como vem da API (GET /despesas). */
export interface DespesaSaida {
  id: number;
  data: string;
  valor: number;
  historico?: string | null;
  descricao?: string | null;
  categoria_id?: number | null;
  categoria_nome?: string | null;
  loja_id?: number | null;
  loja_nome?: string | null;
  viagem_id?: number | null;
  viagem_label?: string | null;
  /** "Viagem #16 · Rio · 08–10/09 · concluida" — o que aparece ao passar o mouse. */
  viagem_titulo?: string | null;
  os_id?: number | null;
  os_codigo?: string | null;
  lancado_por?: string | null;
  origem: OrigemDespesa;
  forma_pgto?: string | null; // código da forma (tbl_app_despesa_formas_pgto)
  loja_cartao_id?: number | null; // cartão de qual loja (Cartão da loja)
  loja_cartao_nome?: string | null;
  restituicao?: string | null; // nao_aplicavel|pendente|restituido (derivado)
  restituido_em?: string | null;
  comprovante?: string | null; // URL pronta para abrir
  lat?: number | null;
  lng?: number | null;
  status?: string | null;
  criado_em?: string | null;
  /** nao_enviada|simulada|enviando|enviada|verificar|ja_no_tiny (ficha #1). */
  envio_tiny?: string | null;
  /** O que o Tiny disse na última conferência: aberto|pago|cancelada|excluida… */
  situacao_tiny?: string | null;
  conta_pagar_tiny?: string | null;
  /** Fora da lista de trabalho desde quando (migration 113). */
  arquivada_em?: string | null;
  /** Parcela (migration 117): a 1ª parcela, "n de N" e o vencimento. */
  despesa_parcelamento_id?: number | null;
  parcela_numero?: number | null;
  parcela_quantidade?: number | null;
  vencimento?: string | null;
}

/** Uma forma de pagamento vinda da API (GET /despesas/formas-pagamento), já FILTRADA por
 *  quem está logado (celular) ou COMPLETA (desktop). Ver migration 052. */
export interface FormaPgtoOpcao {
  codigo: string;
  nome: string;
  descricao?: string | null;
  ressarcivel: boolean; // gera restituição
  pede_loja: boolean; // pede "cartão de qual loja?" (Cartão da loja)
}

/** Formas do FORM PÚBLICO (instalador, sem login) — só as de campo. As telas
 *  logadas puxam a lista filtrada da API; esta fixa serve apenas o público. */
export const FORMAS_PGTO: { value: string; label: string; desc: string }[] = [
  { value: 'banco_inter', label: 'Banco Inter', desc: 'Cartão da empresa (Banco Inter)' },
  { value: 'caixa', label: 'Caixa', desc: 'Dinheiro da empresa que recebi' },
  { value: 'dinheiro_proprio', label: 'Dinheiro próprio', desc: 'Meu dinheiro — preciso ser restituído' },
];

/** Rótulo de QUALQUER forma (inclui cartão próprio / da loja, fora da lista pública). */
const LABEL_FORMA: Record<string, string> = {
  dinheiro_proprio: 'Dinheiro próprio',
  caixa: 'Caixa',
  banco_inter: 'Banco Inter',
  cartao_proprio: 'Cartão próprio',
  cartao_loja: 'Cartão da loja',
};

export const rotuloFormaPgto = (v?: string | null): string =>
  v ? LABEL_FORMA[v] ?? v : '—';

/**
 * A forma de pagamento como cabe numa coluna de lista (24/09). O nome inteiro
 * ("Banco Inter", "Cartão da loja") ocupava mais que o valor da despesa. Aqui
 * o ÍCONE diz se é cartão, dinheiro ou banco, e a palavra diz de quem é. O
 * nome inteiro fica no `title`. O cartão da loja leva a sigla da loja dona
 * ("Cartão BZ") quando a tela a conhece.
 */
const FORMA_CURTA: Record<string, { rotulo: string; icone: string }> = {
  banco_inter: { rotulo: 'Inter', icone: 'bi-bank' },
  caixa: { rotulo: 'Caixa', icone: 'bi-cash-stack' },
  cartao_loja: { rotulo: 'Cartão', icone: 'bi-credit-card' },
  dinheiro_proprio: { rotulo: 'Próprio', icone: 'bi-cash' },
  cartao_proprio: { rotulo: 'Próprio', icone: 'bi-credit-card' },
};

export const formaCurta = (
  v: string,
  siglaLojaCartao?: string | null,
): { rotulo: string; icone: string } => {
  const f = FORMA_CURTA[v] ?? { rotulo: v, icone: 'bi-wallet2' };
  if (v === 'cartao_loja' && siglaLojaCartao) {
    return { ...f, rotulo: `Cartão ${siglaLojaCartao}` };
  }
  return f;
};

/** Badge da forma de pagamento (âmbar = ressarcível; info = dinheiro da empresa). */
export const badgeFormaPgto = (v?: string | null): string => {
  if (v === 'banco_inter') return 'text-bg-primary';
  if (v === 'caixa' || v === 'cartao_loja') return 'text-bg-info';
  if (v === 'dinheiro_proprio' || v === 'cartao_proprio') return 'text-bg-warning';
  return 'text-bg-secondary';
};

export const ORIGENS: { value: OrigemDespesa; label: string }[] = [
  { value: 'campo', label: 'Campo' },
  { value: 'financeiro', label: 'Financeiro' },
];

export const rotuloOrigem = (o: OrigemDespesa): string =>
  ORIGENS.find((x) => x.value === o)?.label ?? o;

/** Variante semântica por origem -> usada pelo helper badge() das telas. */
export const varianteOrigem = (
  o: OrigemDespesa,
): 'success' | 'warning' | 'secondary' => {
  if (o === 'financeiro') return 'success';
  if (o === 'campo') return 'warning';
  return 'secondary';
};

/** 1500.9 -> "R$ 1.500,90" (delega para o helper global; tolera null). */
export const formatarValor = (v: number | null | undefined): string =>
  formatarDinheiro(v);
